import React from 'react';
import {
  Box,
  BoxProps,
  Grid,
  IconProps,
  Link,
  LinkProps,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
} from '@mui/material';

import moment from 'moment';

import ContactMailIcon from '@mui/icons-material/ContactMail';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';

import { TP_COLORS } from '../../../constants';

const footerStyles = {
  padding: '30px',
  backgroundColor: '#1E293B',
};

const typoProps: TypographyProps = {
  variant: 'subtitle1',
  color: TP_COLORS.white,
  marginBottom: '10px',
  fontWeight: '600',
};

const linkProps: LinkProps = {
  width: 'fit-content',
  variant: 'body2',
  underline: 'none',
  color: '#647488',
  marginBottom: '5px',
  sx: { '&:hover': { color: TP_COLORS.white } },
};

const iconProps: IconProps['sx'] = {
  fontSize: 'small',
  style: {
    color: '#647488',
    marginRight: '5px',
  },
};

const socialBoxStyles: BoxProps['sx'] = {
  backgroundColor: '#0F172A',
  color: 'white',
  padding: '10px 20px',
  borderRadius: '30px',
};

const socialIconProps: IconProps['sx'] = {
  fontSize: 'large',
  style: {
    margin: '2px 10px',
  },
};

const stackProps: StackProps = {
  justifyContent: 'center',
  margin: '20px 0px',
};

const copyrightTypoProps: TypographyProps = {
  variant: 'body2',
  color: TP_COLORS.white,
  margin: '5px',
};

const footerItems = [
  {
    title: 'Get Started',
    links: [
      {
        name: 'Sign Up',
        href: 'https://onboarding.tutopiya.com/onboarding/steps',
      },
      { name: 'Learner Guide', href: 'https://www.tutopiya.com/faq/' },
      {
        name: 'Tutor Guide',
        href: 'https://www.tutopiya.com/become-a-tutor/',
      },
    ],
  },
  {
    title: 'Curriculums',
    links: [
      {
        name: 'IGCSE Tuition',
        href: 'https://www.tutopiya.com/igcse-tuition/',
      },
      { name: 'IB Tuition', href: 'https://www.tutopiya.com/ib-tuition/' },
      {
        name: 'PSLE Tuition',
        href: 'https://www.tutopiya.com/psle-results/',
      },
      {
        name: 'Singapore O Level Tuition',
        href: 'https://www.tutopiya.com/o-level-results/',
      },
      {
        name: 'Singapore A Level Tuition',
        href: 'https://www.tutopiya.com/junior-college-singapore-a-level/',
      },
      { name: 'SAT Tuition', href: 'https://www.tutopiya.com/sattuition/' },
    ],
  },
  {
    title: 'Subjects',
    links: [
      {
        name: 'Math Tuition',
        href: 'https://www.tutopiya.com/maths-tuition/',
      },
      {
        name: 'English Tuition',
        href: 'https://www.tutopiya.com/english-tuition/',
      },
      {
        name: 'Science Tuition',
        href: 'https://www.tutopiya.com/science-tuition/',
      },
      {
        name: 'Physics Tuition',
        href: 'https://www.tutopiya.com/science-tuition/',
      },
      {
        name: 'Chemistry Tuition',
        href: 'https://www.tutopiya.com/science-tuition/',
      },
      {
        name: 'Economics Tuition',
        href: 'https://www.tutopiya.com/science-tuition/',
      },
    ],
  },
  {
    title: 'Get Help',
    links: [
      { name: 'Features', href: 'https://www.tutopiya.com/#why-tutopiya' },
      {
        name: 'Privacy policy',
        href: 'https://www.tutopiya.com/home/privacy-policy/',
      },
      {
        name: 'Terms & Conditions',
        href: 'https://www.tutopiya.com/terms-and-conditions/',
      },
    ],
  },
  {
    title: 'About Us',
    links: [
      { name: 'Company', href: 'https://www.tutopiya.com/#why-tutopiya' },
      {
        name: 'Careers',
        href: 'https://www.careers-page.com/tutopiya-pte-ltd',
      },
    ],
  },
];

const Footer: React.FC = () => {
  const year = moment().format('YYYY');

  return (
    <footer style={footerStyles}>
      <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
        {footerItems.map((section, secIndex) => (
          <Grid item key={secIndex} xs={12} sm={4} md={2} lg={2}>
            <Stack paddingLeft={{ xs: '55px', sm: '40px' }}>
              <Typography {...typoProps}>{section.title}</Typography>
              {section.links.map((linkItem, linkIndex) => (
                <Link {...linkProps} key={linkIndex} href={linkItem.href}>
                  {linkItem.name}
                </Link>
              ))}
            </Stack>
          </Grid>
        ))}
      </Grid>
      <Stack
        {...stackProps}
        flexDirection={{ xs: 'column', sm: 'column', md: 'row', lg: 'row' }}
        paddingLeft={{ xs: '55px', sm: '40px' }}
      >
        <Box display="flex" margin="5px">
          <ContactMailIcon {...iconProps} />
          <Typography {...linkProps}>
            22 Changi Business Park Central 2, #02-08, Singapore, 486032
          </Typography>
        </Box>
        <Box display="flex" margin="5px">
          <EmailIcon {...iconProps} />
          <Link {...linkProps} href="mailto:info@tutopiya.com">
            info@tutopiya.com
          </Link>
        </Box>
        <Box display="flex" margin="5px">
          <PhoneIcon {...iconProps} />
          <Link {...linkProps} href="tel:+6560160923">
            (+65) 60160923
          </Link>
        </Box>
      </Stack>
      <Stack {...stackProps} flexDirection="row">
        <Box sx={socialBoxStyles}>
          <Link
            href="https://www.facebook.com/tutopiya/"
            target="_blank"
            color={TP_COLORS.white}
          >
            <FacebookIcon {...socialIconProps} />
          </Link>

          <Link
            href="https://www.linkedin.com/company/tutopiya"
            target="_blank"
            color={TP_COLORS.white}
          >
            <LinkedInIcon {...socialIconProps} />
          </Link>

          <Link
            href="https://twitter.com/tutopiya"
            target="_blank"
            color={TP_COLORS.white}
          >
            <TwitterIcon {...socialIconProps} />
          </Link>

          <Link
            href="   https://www.instagram.com/tutopiya/"
            target="_blank"
            color={TP_COLORS.white}
          >
            <InstagramIcon {...socialIconProps} />
          </Link>
        </Box>
      </Stack>
      <Stack alignItems="center">
        <Typography {...copyrightTypoProps}>All rights reserved</Typography>
        <Typography {...copyrightTypoProps}>
          &copy;{year}&nbsp;Tutopiya
        </Typography>
      </Stack>
    </footer>
  );
};

export default Footer;
